// components/doctor-dashboard/DoctorDashboardHeader.jsx
import React, { useRef } from "react";
import { Activity, User, LogOut } from "lucide-react";
import NotificationBell from "./NotificationBell";
import LogoMikDok from "../common/LogoMikDok";
const styles = {
  shell: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    width: "100%",
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  row: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "0 16px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  brand: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  logo: {
    height: 28,
    width: 28,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
    boxShadow: "0 6px 14px rgba(99,102,241,0.25)",
    flex: "0 0 28px",
  },
  titleWrap: { lineHeight: 1, minWidth: 0 },
  title: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 180,
  },
  right: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  identity: { textAlign: "right" },
  identityName: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 180,
  },
  identityEmail: {
    margin: 0,
    fontSize: 11,
    color: "#6b7280",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 220,
  },
  avatarBtn: {
    height: 32,
    width: 32,
    borderRadius: "50%",
    overflow: "hidden",
    position: "relative",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
    boxShadow: "0 2px 6px rgba(99,102,241,0.25)",
    border: "none",
  },
  logoutBtn: {
    height: 32,
    width: 32,
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: "#6b7280",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },
  spinner: {
    height: 14,
    width: 14,
    border: "2px solid rgba(255,255,255,0.5)",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};

// Simple responsive helper: show name/email when width >= 640px
const useShowIdentity = () => {
  const [show, setShow] = React.useState(typeof window !== "undefined" ? window.innerWidth >= 640 : true);
  React.useEffect(() => {
    const onResize = () => setShow(window.innerWidth >= 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return show;
};

const DoctorHeader = ({
  user,
  doctorProfile,
  uploadingPhoto = false,
  onChangePhoto,
  onLogout,
  onAvatarClick, // <— new: open settings drawer on quick tap/click
}) => {
  const fileInputRef = useRef(null);
  const showIdentity = useShowIdentity();

  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const LONG_PRESS_MS = 500;

  const openPhotoPicker = () => fileInputRef.current?.click();

  // Mouse handlers
  const handleMouseDown = () => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      openPhotoPicker(); // long-press => change photo
    }, LONG_PRESS_MS);
  };
  const handleMouseUp = () => clearTimeout(longPressTimer.current);
  const handleMouseLeave = () => clearTimeout(longPressTimer.current);

  // Touch handlers
  const handleTouchStart = () => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      openPhotoPicker();
    }, LONG_PRESS_MS);
  };
  const handleTouchEnd = () => clearTimeout(longPressTimer.current);
  const handleTouchCancel = () => clearTimeout(longPressTimer.current);

  // Click (quick tap) => open config drawer if provided, otherwise photo picker
  const handleClick = () => {
    // if the long-press already fired, don't also click
    if (longPressTriggered.current) return;
    if (typeof onAvatarClick === "function") onAvatarClick();
    else openPhotoPicker();
  };

  return (
    <header style={styles.shell}>
      <div style={styles.row}>
        {/* Brand */}
        <div style={styles.brand}>
          
          <LogoMikDok
          subtitle="Doctor Page"
          size={35}
          badge={50}
          borderRadius={0}/>
        </div>

        {/* Right controls */}
        <div style={styles.right}>
          <NotificationBell uid={user?.uid} />

          {showIdentity && (
            <div style={styles.identity}>
              <p style={styles.identityName}>
                {doctorProfile?.name || user?.displayName || "Doctor"}
              </p>
              <p style={styles.identityEmail}>{user?.email}</p>
            </div>
          )}

          {/* Avatar (click = open drawer, long-press = change photo) */}
          <button
            type="button"
            title="Profile"
            style={styles.avatarBtn}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
          >
            {doctorProfile?.photoURL ? (
              <img
                src={doctorProfile.photoURL}
                alt="Profile"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <User style={{ color: "#fff", width: 14, height: 14 }} />
            )}
            {uploadingPhoto && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(0,0,0,0.45)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <div style={styles.spinner} />
              </div>
            )}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onChangePhoto}
            style={{ display: "none" }}
          />

          
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </header>
  );
};

export default React.memo(DoctorHeader);
